import { useState, useRef, useEffect, useCallback, useMemo, createElement } from 'react'
import {
  useSensors,
  useSensor,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCorners,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable'
import { CheckCircle2, RotateCcw, ArrowRight } from 'lucide-react'
import { actions } from '@/store'
import { haptics } from '@/lib/haptics'
import { showUndoToast } from '@/lib/toast-utils'
// Helper functions (formerly in index.tsx)
export const buildItemsByStatus = (todos, _statuses) => {
  const itemsByStatus = {}
  if (_statuses && Array.isArray(_statuses)) {
    _statuses.forEach((s) => {
      itemsByStatus[s] = []
    })
  }
  // Ensure todo and done exist as fallbacks/defaults
  if (!itemsByStatus.todo) itemsByStatus.todo = []
  if (!itemsByStatus.done) itemsByStatus.done = []

  todos.forEach((todo) => {
    const s = todo.status
    if (!itemsByStatus[s]) {
      itemsByStatus[s] = []
    }
    itemsByStatus[s].push(todo.id)
  })
  return itemsByStatus
}

const buildIdToStatus = (itemsByStatus) => {
  const idToStatus = new Map()
  Object.entries(itemsByStatus).forEach(([status, ids]) => {
    ids.forEach((id) => {
      idToStatus.set(id, status)
    })
  })
  return idToStatus
}

const getItemsForStatus = (itemsByStatus, status) => {
  return itemsByStatus[status] || []
}

const setItemsForStatus = (itemsByStatus, status, nextItems) => {
  return {
    ...itemsByStatus,
    [status]: nextItems,
  }
}

const setItemsForTwoStatuses = (itemsByStatus, fromStatus, toStatus, nextFrom, nextTo) => {
  return {
    ...itemsByStatus,
    [fromStatus]: nextFrom,
    [toStatus]: nextTo,
  }
}
const arraysEqual = (a, b) => {
  if (a === b) return true
  if (a.length !== b.length) return false
  return a.every((id, index) => id === b[index])
}
const moveSingleItem = (items, oldIndex, overId) => {
  const newIndex = overId ? items.indexOf(overId) : items.length - 1
  if (newIndex === -1 || newIndex === oldIndex) return items
  return arrayMove(items, oldIndex, newIndex)
}
const moveMultipleItems = (items, activeId, overId, draggingIds) => {
  const movingIds = items.filter((id) => draggingIds.has(id))
  if (!movingIds.length) return items
  if (overId && draggingIds.has(overId)) return items
  const remaining = items.filter((id) => !draggingIds.has(id))
  const overIndexInRemaining = overId ? remaining.indexOf(overId) : remaining.length
  if (overId && overIndexInRemaining === -1) return items
  const activeIndexInAll = items.indexOf(activeId)
  const overIndexInAll = overId ? items.indexOf(overId) : items.length - 1
  const insertIndex =
    overId && activeIndexInAll < overIndexInAll ? overIndexInRemaining + 1 : overIndexInRemaining
  const next = [...remaining.slice(0, insertIndex), ...movingIds, ...remaining.slice(insertIndex)]
  return arraysEqual(next, items) ? items : next
}
const moveWithinContainer = (items, activeId, overId, draggingIds) => {
  const oldIndex = items.indexOf(activeId)
  if (oldIndex === -1) return items
  if (draggingIds.size <= 1) return moveSingleItem(items, oldIndex, overId)
  return moveMultipleItems(items, activeId, overId, draggingIds)
}
const moveAcrossContainers = (itemsByStatus, fromContainer, toContainer, overId, draggingIds) => {
  const sourceItems = getItemsForStatus(itemsByStatus, fromContainer)
  const destItems = getItemsForStatus(itemsByStatus, toContainer)

  // Identify which items from the dragging set are currently in the source container
  const movingIds = sourceItems.filter((id) => draggingIds.has(id))
  if (!movingIds.length) return itemsByStatus
  const nextSource = sourceItems.filter((id) => !draggingIds.has(id))

  // Destination: Filter out moving IDs to prevent duplicates if they were somehow already there
  const destWithoutMoving = destItems.filter((id) => !draggingIds.has(id))
  let insertIndex
  const overIndexInDest = overId ? destWithoutMoving.indexOf(overId) : -1
  if (overId && overIndexInDest !== -1) {
    insertIndex = overIndexInDest
  } else if (overId && draggingIds.has(overId)) {
    // If hovering over an item already in the moving set,
    // we should probably just use its relative position if it exists in destItems,
    // but usually dnd-kit handles this better. For now, place it where it was.
    const currentIndex = destItems.indexOf(overId)
    if (currentIndex === -1) {
      insertIndex = destWithoutMoving.length
    } else {
      insertIndex = currentIndex
    }
  } else {
    insertIndex = destWithoutMoving.length
  }
  const nextDest = [
    ...destWithoutMoving.slice(0, insertIndex),
    ...movingIds,
    ...destWithoutMoving.slice(insertIndex),
  ]
  if (arraysEqual(sourceItems, nextSource) && arraysEqual(destItems, nextDest)) {
    return itemsByStatus
  }
  return setItemsForTwoStatuses(itemsByStatus, fromContainer, toContainer, nextSource, nextDest)
}
export function useTaskDragAndDrop(todos, processedTodos, allStatuses, selectedIds) {
  const [activeId, setActiveId] = useState(null)
  const baseItemsByStatus = useMemo(
    () => buildItemsByStatus(processedTodos, allStatuses),
    [processedTodos, allStatuses],
  )
  const [dragItemsByStatus, setDragItemsByStatus] = useState(null)
  const itemsByStatus = dragItemsByStatus ?? baseItemsByStatus
  const [draggingIds, setDraggingIds] = useState(new Set())
  const [dragSizes, setDragSizes] = useState(null)
  const [isDraggingTask, setIsDraggingTask] = useState(false)
  const itemsByStatusRef = useRef(itemsByStatus)
  const idToStatusRef = useRef(buildIdToStatus(itemsByStatus))
  const draggingIdsRef = useRef(draggingIds)
  const dragSnapshotRef = useRef(null)
  const isDraggingRef = useRef(false)
  const pendingUpdateRef = useRef(null)
  const pendingItemsRef = useRef(null)
  const itemNodeRef = useRef(new Map())
  const todosRef = useRef(todos)
  const allStatusesRef = useRef(allStatuses)
  const resizeRafRef = useRef(null)
  const selectedIdsRef = useRef(selectedIds) // Keep ref in sync

  useEffect(() => {
    todosRef.current = todos
  }, [todos])
  useEffect(() => {
    allStatusesRef.current = allStatuses
  }, [allStatuses])
  useEffect(() => {
    draggingIdsRef.current = draggingIds
  }, [draggingIds])
  useEffect(() => {
    selectedIdsRef.current = selectedIds
  }, [selectedIds])
  useEffect(() => {
    itemsByStatusRef.current = itemsByStatus
    idToStatusRef.current = buildIdToStatus(itemsByStatus)
  }, [itemsByStatus])
  const updateItemsByStatus = useCallback((next) => {
    itemsByStatusRef.current = next
    idToStatusRef.current = buildIdToStatus(next)
    setDragItemsByStatus(next)
  }, [])
  const scheduleItemsByStatus = useCallback((next) => {
    itemsByStatusRef.current = next
    idToStatusRef.current = buildIdToStatus(next)
    pendingItemsRef.current = next
    if (pendingUpdateRef.current !== null) return
    pendingUpdateRef.current = requestAnimationFrame(() => {
      pendingUpdateRef.current = null
      const pending = pendingItemsRef.current
      if (pending) {
        setDragItemsByStatus(pending)
        pendingItemsRef.current = null
      }
    })
  }, [])
  const registerItem = useCallback((id, node) => {
    if (node) {
      itemNodeRef.current.set(id, node)
    } else {
      itemNodeRef.current.delete(id)
    }
  }, [])
  const captureDragSizes = useCallback(() => {
    const next = new Map()
    itemNodeRef.current.forEach((node, id) => {
      const rect = node.getBoundingClientRect()
      next.set(id, {
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      })
    })
    setDragSizes(next)
  }, [])
  const resetDragState = useCallback(
    (restoreSnapshot) => {
      if (pendingUpdateRef.current !== null) {
        cancelAnimationFrame(pendingUpdateRef.current)
        pendingUpdateRef.current = null
      }
      pendingItemsRef.current = null
      if (restoreSnapshot && dragSnapshotRef.current) {
        updateItemsByStatus(dragSnapshotRef.current)
      } else {
        setDragItemsByStatus(null)
      }
      dragSnapshotRef.current = null
      setDragSizes(null)
      isDraggingRef.current = false
      setActiveId(null)
      setDraggingIds(new Set())
      draggingIdsRef.current = new Set()
      setIsDraggingTask(false)
    },
    [updateItemsByStatus],
  )

  // Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 120,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Trello-like collision detection strategy
  const collisionDetection = useCallback((args) => {
    // 1. First, find which container we are hovering.
    // Priority: Pointer directly over container > Pointer over item in container > Rect overlap with container
    const pointerCollisions = pointerWithin(args)
    const rectCollisions = rectIntersection(args)

    // Find the container ID from pointer collisions
    let containerId = pointerCollisions.find((c) => c.data?.type === 'container')?.id

    // If not over a container directly, check if over an item and get its container
    if (!containerId) {
      const itemCollision = pointerCollisions.find((c) => c.data?.type === 'item')
      if (itemCollision) {
        containerId = itemCollision.data?.containerId
      }
    }

    // Fallback: If pointer is nowhere, check rect intersections (dragging large card over container)
    if (!containerId) {
      containerId = rectCollisions.find((c) => c.data?.type === 'container')?.id
    }

    // 2. If we found a container, constrain collision detection to items in that container
    if (containerId) {
      // Strip the "container:" prefix if it exists to get the status
      const status = containerId.replace('container:', '')
      const itemsInContainer = getItemsForStatus(itemsByStatusRef.current, status)
      const droppableItems = args.droppableContainers.filter((c) => itemsInContainer.includes(c.id))

      // A. Priority: Pointer directly over an item
      const pointerItemCollisions = pointerWithin({
        ...args,
        droppableContainers: droppableItems,
      })
      if (pointerItemCollisions.length > 0) {
        return pointerItemCollisions
      }

      // B. Secondary: Closest corners (better for variable heights)
      const containerItemCollisions = closestCorners({
        ...args,
        droppableContainers: droppableItems,
      })

      // If we found a closest item, return it
      if (containerItemCollisions.length > 0) {
        return containerItemCollisions
      }

      // C. Fallback: Return the container itself
      const containerCollision =
        pointerCollisions.find((c) => c.id === containerId) ||
        rectCollisions.find((c) => c.id === containerId)
      if (containerCollision) {
        return [containerCollision]
      }
    }

    // 3. Last Resort: Standard closestCorners
    return closestCorners(args)
  }, [])
  const handleDragStart = useCallback(
    (event) => {
      const nextActiveId = event.active.id
      setActiveId(nextActiveId)
      setIsDraggingTask(true)
      isDraggingRef.current = true
      setDragItemsByStatus(itemsByStatusRef.current)
      dragSnapshotRef.current = itemsByStatusRef.current
      captureDragSizes()
      const activeContainer = idToStatusRef.current.get(nextActiveId)
      const selected = selectedIdsRef.current
      let nextDraggingIds
      if (activeContainer && selected.has(nextActiveId) && selected.size > 1) {
        nextDraggingIds = new Set(
          Array.from(selected).filter((id) => idToStatusRef.current.get(id) === activeContainer),
        )
      } else {
        nextDraggingIds = new Set([nextActiveId])
      }
      setDraggingIds(nextDraggingIds)
      draggingIdsRef.current = nextDraggingIds
      haptics.impact()
    },
    [captureDragSizes],
  )
  const handleDragOver = useCallback(
    (event) => {
      const { active, over } = event
      if (!over) return
      const eventActiveId = active.id
      const overId = over.id
      const activeContainer = idToStatusRef.current.get(eventActiveId)
      const overContainer = over.data.current?.containerId || idToStatusRef.current.get(overId)
      if (!activeContainer || !overContainer) return
      const draggingSet = draggingIdsRef.current.size
        ? draggingIdsRef.current
        : new Set([eventActiveId])
      const overIsContainer = over.data.current?.type === 'container'
      const resolvedOverId = overIsContainer ? null : overId
      if (activeContainer === overContainer) {
        const currentItems = getItemsForStatus(itemsByStatusRef.current, activeContainer)
        const nextItems = moveWithinContainer(
          currentItems,
          eventActiveId,
          resolvedOverId,
          draggingSet,
        )
        if (nextItems === currentItems) return
        haptics.impact()
        scheduleItemsByStatus(
          setItemsForStatus(itemsByStatusRef.current, activeContainer, nextItems),
        )
        return
      }
      const next = moveAcrossContainers(
        itemsByStatusRef.current,
        activeContainer,
        overContainer,
        resolvedOverId,
        draggingSet,
      )
      if (next === itemsByStatusRef.current) return
      haptics.impact()
      scheduleItemsByStatus(next)
    },
    [scheduleItemsByStatus],
  )
  const handleDragEnd = useCallback(
    (event) => {
      const { over } = event
      if (!over) {
        haptics.impact()
        resetDragState(true)
        return
      }
      try {
        const allTodos = todosRef.current
        const currentAllStatuses = allStatusesRef.current
        const todoMap = new Map(allTodos.map((todo) => [todo.id, todo]))

        // Get the set of IDs that were part of the drag-and-drop view (visible items)
        const visibleIds = new Set()
        Object.values(itemsByStatusRef.current).forEach((ids) => {
          ids.forEach((id) => visibleIds.add(id))
        })

        // Maintain a list of tasks that were NOT visible so we don't delete them
        const hiddenTodos = allTodos.filter((t) => !visibleIds.has(t.id))

        // Reconstruct the visible part of the todos list based on the optimistic UI state
        const nextVisibleTodos = []
        let statusChangedCount = 0
        let lastStatusMovedTo = ''

        currentAllStatuses.forEach((status) => {
          const ids = getItemsForStatus(itemsByStatusRef.current, status)
          ids.forEach((id) => {
            const todo = todoMap.get(id)
            if (!todo) return
            const nextCompleted = status === 'done'
            const hasChanged = todo.status !== status || todo.completed !== nextCompleted

            if (hasChanged) {
              statusChangedCount++
              lastStatusMovedTo = status
              nextVisibleTodos.push({
                ...todo,
                status,
                completed: nextCompleted,
              })
            } else {
              nextVisibleTodos.push(todo)
            }
          })
        })

        // Final list is the combination of reordered visible tasks and preserved hidden tasks
        const nextTodos = [...nextVisibleTodos, ...hiddenTodos]
        const isSameOrder =
          nextTodos.length === allTodos.length &&
          nextTodos.every((todo, index) => {
            const current = allTodos[index]
            return (
              current.id === todo.id &&
              current.status === todo.status &&
              current.completed === todo.completed
            )
          })

        if (!isSameOrder) {
          haptics.success()
          actions.setTodos(nextTodos)

          let message = 'Tasks reordered'
          let icon = createElement(ArrowRight, { className: 'h-4 w-4 text-primary' })

          if (statusChangedCount > 0) {
            if (statusChangedCount === 1) {
              if (lastStatusMovedTo === 'done') {
                message = 'Task completed'
                icon = createElement(CheckCircle2, { className: 'h-4 w-4 text-primary' })
              } else if (lastStatusMovedTo === 'todo') {
                message = 'Task reopened'
                icon = createElement(RotateCcw, { className: 'h-4 w-4 text-primary' })
              } else {
                message = `Moved to ${lastStatusMovedTo.charAt(0).toUpperCase() + lastStatusMovedTo.slice(1)}`
              }
            } else {
              message = `${statusChangedCount} tasks updated`
            }
          }

          showUndoToast(message, icon)
        }
      } finally {
        resetDragState(false)
      }
    },
    [resetDragState],
  )

  // Resize observer logic for drag sizes
  useEffect(() => {
    if (!activeId) {
      return () => {}
    }
    const handleResize = () => {
      if (!isDraggingRef.current) return
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current)
      }
      resizeRafRef.current = requestAnimationFrame(() => {
        resizeRafRef.current = null
        if (isDraggingRef.current) {
          captureDragSizes()
        }
      })
    }
    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current)
    }
  }, [activeId, captureDragSizes])

  // Cleanup
  useEffect(() => {
    return () => {
      if (pendingUpdateRef.current !== null) {
        cancelAnimationFrame(pendingUpdateRef.current)
        pendingUpdateRef.current = null
      }
    }
  }, [])
  const todoMap = useMemo(() => new Map(todos.map((todo) => [todo.id, todo])), [todos])
  const activeTodo = activeId ? todoMap.get(activeId) || null : null
  const activeDragSize = activeId ? (dragSizes?.get(activeId) ?? null) : null
  const isDragActive = Boolean(activeId)
  return {
    activeId,
    setActiveId,
    itemsByStatus,
    draggingIds,
    isDraggingTask,
    setIsDraggingTask,
    dragSizes,
    activeTodo,
    activeDragSize,
    isDragActive,
    sensors,
    collisionDetection,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    resetDragState,
    registerItem,
    todoMap,
  }
}
